import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';
import Logo from '../components/Logo';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { normalizePhone, validateOtpCode } from '../utils/inputValidation';

// 🔥 CAMBIADO A 4 DÍGITOS
const CODE_LENGTH = 4;
const CODE_TIME = 300; // 5 minutos

const VerifyResetCode = () => {
  const navigate = useNavigate();
  const { resetPhone } = useApp();
  const phone = resetPhone || null;
  const [codeDigits, setCodeDigits] = useState(Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef([]);

  const [codeVerified, setCodeVerified] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);

  // 🔥 ESTADO DE INTENTOS
  const [attempts, setAttempts] = useState(0);

  const [secondsLeft, setSecondsLeft] = useState(CODE_TIME);
  const timerRef = useRef(null);

  const codeInput = codeDigits.join('');

  /* VALIDACIÓN TELÉFONO */
  useEffect(() => {
    if (!phone) {
      navigate('/forgot-password');
    }
  }, [phone, navigate]);

  /* TIMER */
  useEffect(() => {
    if (secondsLeft > 0 && !codeVerified) {
      timerRef.current = setTimeout(() => {
        setSecondsLeft(s => s - 1);
      }, 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [secondsLeft, codeVerified]);

  /* AUTO-VERIFICAR CUANDO SE LLENAN LOS 4 DÍGITOS */
  useEffect(() => {
    if (codeInput.length === CODE_LENGTH && !codeVerified && !loading) {
      handleVerifyCode(codeInput);
    }
  }, [codeInput]); // eslint-disable-line

  /* ===================== CODE INPUT HANDLERS ===================== */
  const handleDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...codeDigits];
    updated[index] = value;
    setCodeDigits(updated);
    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const newDigits = pasted.split('');
    while (newDigits.length < CODE_LENGTH) newDigits.push('');
    setCodeDigits(newDigits);
  };

  const resetCode = () => {
    setCodeDigits(Array(CODE_LENGTH).fill(''));
    inputsRef.current[0]?.focus();
  };

  /* ===================== VERIFICAR CÓDIGO ===================== */
  const handleVerifyCode = async finalCode => {
    setLoading(true);
    setMessage('');

    try {
      const { data, error } = await supabase.rpc('verify_password_reset_otp', {
        p_phone: normalizePhone(phone),
        p_code: validateOtpCode(finalCode)
      });

      if (error) throw error;

      const result = typeof data === 'string' ? JSON.parse(data) : data;

      if (result.error === 'not_found') {
        setMessage('Sesión expirada o código no encontrado.');
        setMessageType('error');
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      if (result.error === 'expired') {
        setMessage('El código ha expirado.');
        setMessageType('error');
        resetCode();
        return;
      }

      if (result.error === 'locked') {
        setMessage('Demasiados intentos fallidos. Proceso cancelado.');
        setMessageType('error');
        setTimeout(() => navigate('/forgot-password'), 3000);
        return;
      }

      if (result.error === 'invalid') {
        setAttempts(3 - (result.attempts_left ?? 0));
        setMessage(`Código incorrecto. Te quedan ${result.attempts_left ?? 0} intentos.`);
        setMessageType('error');
        resetCode();
        return;
      }

      setCodeVerified(true);
      setMessage('Código verificado correctamente.');
      setMessageType('success');
      clearTimeout(timerRef.current);

      setTimeout(() => {
        navigate('/reset-password');
      }, 1000);
    } catch (err) {
      setMessage('Error al verificar el código.');
      setMessageType('error');
      resetCode();
    } finally {
      setLoading(false);
    }
  };

  /* 🔥 REENVIAR CÓDIGO CON ANTI-SPAM */
  const handleResendCode = async () => {
    // 🔥 CAPA 2 ANTI-SPAM
    const localAttempts = parseInt(localStorage.getItem('otp_attempts') || '0');
    if (localAttempts >= 3) {
      setMessage('Límite máximo de códigos superado por hoy.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const { error: otpError } = await supabase.rpc('resend_password_reset_otp', {
        p_phone: normalizePhone(phone)
      });

      if (otpError) {
        if (otpError.message?.includes('Límite de seguridad') || otpError.code === 'P0001') {
          localStorage.setItem('otp_attempts', '3');
          throw new Error('Has superado el límite diario de códigos para este número.');
        }
        throw otpError;
      }

      localStorage.setItem('otp_attempts', (localAttempts + 1).toString());
      setSecondsLeft(CODE_TIME);
      setAttempts(0); // Reinicia intentos de escritura manuales
      resetCode();
      setMessage('Código reenviado correctamente.');
      setMessageType('success');
    } catch (err) {
      console.error(err);
      setMessage(err.message || 'Error al reenviar el código.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

   /* 🕒 FORMATO TIEMPO */
  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <Layout showNavigation={false}>
      <div className="min-h-screen px-6 py-12 bg-gray-50 flex flex-col justify-center">
        <motion.div
          className="max-w-md mx-auto w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-10">
            <Logo size="large" className="mx-auto mb-8" />
          </div>

          <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 p-8 relative overflow-hidden">
            
            <AnimatePresence mode="wait">
              {message && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`mb-6 p-4 rounded-2xl flex items-center gap-3 border ${
                    messageType === 'success' 
                      ? 'bg-green-50 border-green-100 text-green-700' 
                      : 'bg-red-50 border-red-100 text-red-700'
                  }`}
                >
                  {messageType === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                  <p className="text-sm font-bold tracking-tight leading-tight">{message}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {!codeVerified ? (
              <>
                <h2 className="text-2xl font-bold text-center mb-3 text-slate-800">
                  Verifica tu identidad
                </h2>
                <p className="text-center text-slate-500 mb-6 leading-relaxed">
                  Ingresa el código de 4 dígitos enviado por WhatsApp al<br />
                  <strong className="text-lg text-slate-800">{phone}</strong>
                </p>

                {/* 🔥 CUADRITOS INDIVIDUALES AJUSTADOS A 4 */}
                <div className="flex justify-center gap-4 mb-8">
                  {codeDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => (inputsRef.current[i] = el)}
                      type="text"
                      value={digit}
                      onChange={e => handleDigitChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      onPaste={handlePaste}
                      maxLength={1}
                      className="w-16 h-16 text-3xl font-bold text-center border-2 border-slate-200 rounded-xl focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-200 bg-slate-50 focus:bg-white"
                      disabled={loading || attempts >= 3}
                    />
                  ))}
                </div>

                <div className="flex justify-between items-center text-sm pt-4 border-t border-slate-100">
                  <span className="text-slate-500 font-medium">
                    {formatTime(secondsLeft)}
                  </span>
                  {secondsLeft === 0 && attempts < 3 && (
                    <button 
                      onClick={handleResendCode} 
                      disabled={loading} 
                      className="text-blue-600 font-bold hover:text-blue-800 disabled:text-slate-400 transition-colors"
                    >
                      Reenviar código
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 text-green-500">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">¡Verificado!</h2>
                <p className="text-slate-500 font-medium mt-2">Redirigiendo para cambiar tu contraseña...</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default VerifyResetCode;

